<script>
  //// imports
  import { ROUTES } from "@utils/routes.js";
  import { onMount } from "svelte";
  import { writable } from "svelte/store";
  import { pmfilters } from "lib/stores.js";
  import { fetchWithSession } from "../../../../lib/util/fetchWithSession";
  import { activitiesStore } from "@stores/activitiesStores.js";
  import { filtersConfigStore } from "@stores/filtersConfigStores.js";
  import { getApplicableFilters } from "../../../../lib/util/getApplicableFilters";

  import Filter from "./components/filter/Filter.svelte";

  import {
    Accordion,
    SectionWrapper,
    Button,
    TextInput,
  } from "paxray-components-svelte";
  import AddToActivity from "./components/AddToActivity.svelte";
  import FlowPlotter from "./components/FlowPlotter.svelte";
  import Header from "lib/components/Header.svelte";
  import ProcessSelection from "./components/processSelection.svelte";
  import ContentSwitcher from "lib/components/ContentSwitcher.svelte";
  import Modal from "lib/components/Modal.svelte";
  import { useSvelteFlow } from "@xyflow/svelte";
  import CasesRightMenu from "./components/CasesRightMenu.svelte";
  import FlowPlotterCase from "./components/FlowPlotterCase.svelte";
  import ProgramInfo from "./components/ProgramInfo.svelte";
  import { Loading } from "carbon-components-svelte";
  import Paxbot from "lib/components/paxbot/paxbotBottomRight.svelte";

  import FilterToast from "./components/FilterToast.svelte";
  import {
    FIT_SCREEN_ICON,
    FULLSCREEN_ICON,
    MAP_ICON,
    SQUARE_LANDSCAPE_ICON,
  } from "@utils/icons.js";

  const { setCurrentView } = filtersConfigStore;
  //// data
  export let analysisid;

  // temp data
  let processes = [];
  let selectedClusters = [];
  let casesSelected = false;
  let seeMinimap = false;
  let saveOption;
  let mergingSplitting = false;
  let loading = true;
  let loadingCases = true;
  let activities = [];
  let paxbotVisible = false;
  let paxbotMessage = "";
  let paxbotTitle = "";
  let paxbotExplanation = [];
  let tabOptions = ["Activities", "Windows", "Interactions", "Cases"];
  let selectedProcesses = [];
  let firstGraphLoad = true;

  const { fitView, zoomIn, zoomOut, getZoom } = useSvelteFlow();
  const { getAllActivities } = activitiesStore;
  let zoomLevel = writable((getZoom() * 100).toFixed(0));
  let initialZoomValue = writable(0);
  let fullscreen = false;

  let selectedNode = "";
  let selectedNodes = [];
  let expandedNodes = [];
  let fullGraphView;
  let fullNodeInfo = true;
  let triggerUpdate = false;
  let nvariants = 1;
  let minFrequency = 0;
  let graph = {
    activityMappingCluster: [],
    allactivitymapping: [],
    edges: [],
    executionFrequency: 0,
    nactivities: 0,
    nodes: [],
    nusers: 0,
    nvariants: 0,
    timepercase: 0,
    totaltime: 0,
    uniquePrograms: [],
    uniqueActivities: 0,
    variants: [],
  };
  let casesGraph = {
    nodes: [],
    edges: [],
  };
  let nvar = [1, 5];
  let editProcessModalOpen = false;
  let editProcessId = 0;
  let editProcessName = "";
  let clusteringModalOpen = false;
  let nprocesses = 2;
  let previousView;
  let showToast = false;
  let allActivities;

  let currentClusters = selectedClusters;
  let currentExpandedNodes = [];
  let activitiesToShow = [];

  let flowPlotterElement;
  let flowPlotterCaseElement;
  let currentAnalysisId = null;

  $: {
    selectedClusters = selectedProcesses.map((process) => process.id);
  }

  $: casesSelected = fullGraphView === 3;

  $: setCurrentView("Process Explorer", tabOptions[fullGraphView]);

  $: {
    if (!saveOption) {
      saveOption = getLocalStorage();
      fullGraphView = saveOption;
    }
  }

  $: {
    if (selectedClusters.length === 0 && graph.allactivitymapping.length > 0) {
      resetGraph();
    }
  }

  // activities was assigned three times in a row: destructured from the store,
  // immediately overwritten by the graph, then mapped over itself. The store
  // value was never used. One derivation from its two real inputs instead.
  $: ({ allActivities } = $activitiesStore);

  $: activities = (graph["allactivitymapping"] || []).map((activity) => {
    const updatedActivity = (allActivities || []).find(
      (a) => a.id === activity.activityid,
    );
    return updatedActivity ? { ...activity, ...updatedActivity } : activity;
  });

  $: nodesData = graph["nodes"];

  $: if (analysisid) {
    const isStoreEmpty =
      !$activitiesStore.allActivities ||
      $activitiesStore.allActivities.length === 0;
    if (analysisid !== currentAnalysisId || isStoreEmpty) {
      currentAnalysisId = analysisid;
      getAllActivities(analysisid);
    }
  }

  $: if (fullGraphView !== previousView && fullGraphView !== undefined) {
    selectedProcesses = [];
    previousView = fullGraphView;
    selectedClusters = [];
    const stored = getClustersFromLocalStorage(fullGraphView);
    if (stored.length > 0) {
      selectedProcesses = stored;
    }
    triggerUpdate = true;
  }

  $: {
    if (selectedClusters.length === 0 && graph.allactivitymapping.length > 0) {
      graph = {
        activityMappingCluster: [],
        allactivitymapping: [],
        edges: [],
        executionFrequency: 0,
        nactivities: 0,
        nodes: [],
        nusers: 0,
        nvariants: 0,
        timepercase: 0,
        totaltime: 0,
        uniquePrograms: [],
        uniqueActivities: 0,
        variants: [],
      };
    }
  }

  $: updateCurrentClusters(selectedClusters);
  $: updateCurrentExpandedNodes(expandedNodes);

  $: if ($filtersConfigStore.storeLoaded && allActivities.length > 0) {
    updateGraph(
      $pmfilters,
      fullGraphView,
      currentClusters,
      currentExpandedNodes,
      allActivities,
    );
  }

  $: if (fullGraphView === 3 && nvariants) {
    updateGraph(
      $pmfilters,
      fullGraphView,
      currentClusters,
      currentExpandedNodes,
      allActivities,
    );
  }

  $: {
    if (selectedClusters !== undefined) {
      updateSelectedClustersInLocalStorage(fullGraphView, selectedProcesses);
    }
  }

  function updateGraph(
    $pmfilters,
    fullGraphView,
    selectedClusters,
    expandedNodes,
  ) {
    if (firstGraphLoad) {
      firstGraphLoad = false;
    }
    if (fullGraphView === 3) {
      resetGraph();
      processGraphView(nvariants, $pmfilters[analysisid]);
    } else {
      if (fullGraphView === 1) {
        windowsAutomationTraces();
      } else {
        automationTraces();
      }
    }

    if (fullGraphView < 3 || triggerUpdate) {
      loadCluster(selectedClusters, fullGraphView, expandedNodes);
    }

    setLocalStorage(fullGraphView);
    firstGraphLoad = false;
  }

  function updateCurrentClusters(selectedClusters) {
    if (!arraysAreEqual(currentClusters, selectedClusters)) {
      currentClusters = [];
      selectedClusters.forEach((element) => {
        currentClusters.push(element);
      });
    }
  }

  function arraysAreEqual(arr1, arr2) {
    if (arr1.length !== arr2.length) return false;
    return arr1.every((value, index) => value === arr2[index]);
  }

  function updateCurrentExpandedNodes(expandedNodes) {
    if (!arraysAreEqual(currentExpandedNodes, expandedNodes)) {
      currentExpandedNodes = [];
      expandedNodes.forEach((element) => {
        currentExpandedNodes.push(element);
      });
    }
  }

  function formatPaxbotMessage() {
    paxbotExplanation = [];
    let text = paxbotMessage.split("\n");
    let help = "";
    text.forEach((element) => {
      if (element.includes("**")) {
        if (element.endsWith("**") || element.endsWith("** ")) {
          help = element.split("**");
          let c = "<Strong>";
          help.forEach((h) => {
            c += h;
          });
          c += "</Strong>";
          paxbotExplanation.push(c);
        } else {
          help = element.split("**");
          if (help.length === 1) {
            paxbotExplanation.push("<Strong>" + help[0] + "</Strong>");
          } else if (help.length === 2) {
            paxbotExplanation.push(
              "<Strong>" + help[0] + "</Strong>" + help[1],
            );
          } else if (help.length === 3) {
            paxbotExplanation.push(
              help[0] + "<Strong>" + help[1] + "</Strong>" + help[2],
            );
          }
        }
      } else {
        paxbotExplanation.push(element);
      }
    });
  }

  function autoname() {
    editProcessName = activities
      .map((e) => {
        return e.activityname;
      })
      .join(", ");
  }

  function processGraphView(nvariants, pmfilters) {
    const applicableFilters = getApplicableFilters(pmfilters);
    const filterString = JSON.stringify(applicableFilters);
    setLocalStorage(true);
    getCasesProcessGraph(nvariants, filterString).then(() => {
      updateZoomLevel().then(() => {
        fitView();
      });
    });
  }

  function toggleFullscreen() {
    fullscreen = !fullscreen;
  }

  async function incrementZoom() {
    await zoomIn();
    updateZoomLevel();
  }

  async function decrementZoom() {
    await zoomOut();
    updateZoomLevel();
  }

  async function updateZoomLevel() {
    zoomLevel.set((getZoom() * 100).toFixed(0));
  }

  function automationTraces() {
    const applicableFilters = getApplicableFilters($pmfilters[analysisid]);
    const filterString = JSON.stringify(applicableFilters);
    fetchWithSession(
      `/api/analysis/automationTraces/${analysisid}?filter=${filterString}`,
    )
      .then((response) => response.json())
      .then((data) => {
        processes = data;
        if (data.length === 0 && applicableFilters.length > 0) showToast = true;
        else {
          showToast = false;
        }
        //here
      });
  }

  function windowsAutomationTraces() {
    const applicableFilters = getApplicableFilters($pmfilters[analysisid]);
    const filterString = JSON.stringify(applicableFilters);
    fetchWithSession(
      `/api/analysis/apps/automationTraces/${analysisid}?filter=${filterString}`,
    )
      .then((response) => response.json())
      .then((data) => {
        processes = data;
        if (data.length === 0 && applicableFilters.length > 0) showToast = true;
        else {
          showToast = false;
        }
      });
  }

  function loadCluster(clusterids, fullGraphView = false, expandedNodes = []) {
    if (casesSelected) return;
    resetGraph();
    const applicableFilters = getApplicableFilters($pmfilters[analysisid]);
    const filterString = JSON.stringify(applicableFilters);
    const url =
      fullGraphView === 1
        ? "/api/analysis/apps/getAutomationTraces?analysisid="
        : "/api/analysis/getAutomationTraces?analysisid=";
    if (clusterids && clusterids.length) {
      fetchWithSession(
        url +
          analysisid +
          `&fullgraph=` +
          (fullGraphView === 2) +
          `&clusterids=` +
          JSON.stringify(clusterids) +
          `&expandednodes=` +
          JSON.stringify(expandedNodes) +
          `&filter=${filterString}`,
      )
        .then((response) => response.json())
        .then((data) => {
          graph = data;
          fitView();
          if (
            (Object.keys(data).length === 0 || data.nodes.length === 0) &&
            applicableFilters.length > 0
          ) {
            showToast = true;
            resetGraph();
          } else {
            showToast = false;
          }

          // saveClustersToLocalStorage(fullGraphView, cluster);
          triggerUpdate = false;
          loading = false;
          setTimeout(() => {
            fitView();
          }, 100);
        });
    }
  }
  function autorenameClusters() {
    fetchWithSession(`/api/analysis/apps/autorenameCluster`, {
      method: "POST",
      body: JSON.stringify({
        analysisid: analysisid,
        clusters: selectedClusters,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        processes = data;
        automationTraces();
      });
  }

  function resetGraph() {
    graph = {
      activityMappingCluster: [],
      allactivitymapping: [],
      edges: [],
      executionFrequency: 0,
      nactivities: 0,
      nodes: [],
      nusers: 0,
      nvariants: 0,
      timepercase: 0,
      totaltime: 0,
      uniquePrograms: [],
      uniqueActivities: 0,
      variants: [],
    };
    casesGraph = {
      nodes: [],
      edges: [],
    };
  }

  // fullscreen
  function mergeProcessesApp(clusterids) {
    mergingSplitting = true;
    fetchWithSession(`/api/analysis/apps/applyClustering`, {
      method: "POST",
      body: JSON.stringify({
        analysisid: analysisid,
        clusters: clusterids,
        nclusters: nprocesses,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        selectedClusters = [];
        if (data.status == "success") {
          if (nprocesses === 1) {
            windowsAutomationTraces();
            let newProc = -1;
            processes.forEach((p) => {
              if (Number(p.id) > Number(newProc)) {
                newProc = p.id;
              }
            });
            selectedClusters = [newProc];
          } else selectedClusters = [];
        }
      })
      .finally(() => {
        mergingSplitting = false;
      });
  }

  function mergeProcesses(clusterids) {
    mergingSplitting = true;
    fetchWithSession(`/api/analysis/applyClustering`, {
      method: "POST",
      body: JSON.stringify({
        analysisid: analysisid,
        clusters: clusterids,
        nclusters: nprocesses,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        selectedClusters = [];
        if (data.status == "success") {
          automationTraces();
          if (nprocesses === 1) {
            let newProc = -1;
            processes.forEach((p) => {
              if (Number(p.id) > Number(newProc)) {
                newProc = p.id;
              }
            });
            selectedClusters = [newProc];
          } else selectedClusters = [];
        }
      })
      .finally(() => {
        mergingSplitting = false;
      });
  }

  function renameCluster() {
    fetchWithSession(`/api/analysis/renameCluster`, {
      method: "POST",
      body: JSON.stringify({
        analysisid: analysisid,
        cluster: editProcessId,
        name: editProcessName,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status == "success") {
          automationTraces();
        }
      });
  }

  function renameClusterApp() {
    fetchWithSession(`/api/analysis/apps/renameCluster`, {
      method: "POST",
      body: JSON.stringify({
        analysisid: analysisid,
        cluster: editProcessId,
        name: editProcessName,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status == "success") {
          windowsAutomationTraces();
        }
      });
  }
  async function getCasesProcessGraph(nvariants, pmfilters) {
    resetGraph();
    fetchWithSession(
      `/api/analysis/cases/getProcessGraph/${analysisid}?nvarstart=${0}&nvarend=${nvariants}&filter=${pmfilters}`,
    )
      .then((response) => response.json())
      .then((data) => {
        resetGraph();
        casesGraph = data;
        if (data.nodes.length === 0 && pmfilters !== "[]") showToast = true;
        else {
          showToast = false;
        }
      })
      .then(() => {
        updateZoomLevel();
        fitView();
      })
      .then(() => {
        loadingCases = false;
      });
  }

  function setLocalStorage(value) {
    let selectedView = JSON.parse(localStorage.getItem("selectedView")) || {};
    if (selectedView[analysisid]) {
      selectedView[analysisid] = value;
    } else {
      selectedView[analysisid] = value;
    }
    localStorage.setItem("selectedView", JSON.stringify(selectedView));
  }

  function saveClustersToLocalStorage(tab, clusters) {
    let storedClusters =
      JSON.parse(localStorage.getItem("selectedClustersByTab")) || {};
    if (!storedClusters[analysisid]) storedClusters[analysisid] = {};
    storedClusters[analysisid][tab] = clusters;
    localStorage.setItem(
      "selectedClustersByTab",
      JSON.stringify(storedClusters),
    );
  }

  function getLocalStorage() {
    const selectedView = JSON.parse(localStorage.getItem("selectedView"));
    if (!selectedView) return 1;
    if (selectedView[analysisid] !== undefined) {
      return selectedView[analysisid];
    } else {
      return 1;
    }
  }

  function getClustersFromLocalStorage(tab) {
    const storedClusters = JSON.parse(
      localStorage.getItem("selectedClustersByTab"),
    );
    return storedClusters?.[analysisid]?.[tab] || [];
  }

  function updateSelectedClustersInLocalStorage(tab, clusters) {
    let storedClusters =
      JSON.parse(localStorage.getItem("selectedClustersByTab")) || {};

    if (!storedClusters[analysisid]) {
      storedClusters[analysisid] = {};
    }

    if (clusters.length === 0) {
      delete storedClusters[analysisid][tab];
    } else {
      storedClusters[analysisid][tab] = clusters;
    }

    localStorage.setItem(
      "selectedClustersByTab",
      JSON.stringify(storedClusters),
    );
  }

  onMount(() => {
    setTimeout(() => {
      const initialZoom = (getZoom() * 100).toFixed(0);
      zoomLevel.set(initialZoom);
      initialZoomValue.set(initialZoom);
    }, 500);
  });
</script>

<FilterToast bind:showToast></FilterToast>
<!-- <FilterToast {showToast} on:closeToast={showToast=false}></FilterToast> -->

<!-- Paxbot -->
<Paxbot bind:paxbotOpen={paxbotVisible} heading={paxbotTitle}>
  {#each paxbotMessage as annotation}
    <div
      class="task_wrapper"
      style="display: flex; flex-direction:column; color: white;"
    >
      <strong>{annotation.task}</strong>
      <div>{annotation.description}</div>
    </div>
  {/each}
</Paxbot>

{#if casesSelected}
  <Header section="Object" breadcrumb="Process Explorer">
    <!-- <ButtonNew
      secondary={true}
      text="Generate Process Model"
      on:click={() => {
        generateProcessModel();
      }}
    /> -->
    <!-- <PmFilter /> -->
    <Filter />
  </Header>
{:else}
  <Header section="Workflow Analysis" breadcrumb="Process Explorer">
    <Button
      text="App Mapping"
      on:click={() => {
        window.open(`${ROUTES.dam.appnamemapping}`, "_blank");
      }}
      kind="secondary"
    />
    <Button
      text="Activity Mapping"
      on:click={() => {
        window.open(
          `${ROUTES.dam.mappingAssistant}?view=activitymapping&analysisid=${analysisid}`,
          "_blank",
        );
      }}
      kind="secondary"
    />
    <Button
      text="Autorename"
      on:click={() => {
        autorenameClusters();
      }}
      kind="secondary"
    />
    <Filter />
  </Header>
{/if}

<SectionWrapper {fullscreen} fitScreen={true}>
  <div class="main_wrapper" slot="main">
    <div class="canvas">
      {#if casesSelected}
        {#if loadingCases}
          <div class="loading_wrapper">
            <Loading withOverlay={false} />
          </div>
        {:else}
          {#key `${minFrequency} - ${nvariants}`}
            <FlowPlotterCase
              bind:this={flowPlotterCaseElement}
              nodesData={casesGraph["nodes"]}
              edgesData={casesGraph["edges"]}
              width="100%"
              height="calc(100% - 50px)"
              bind:selectedNode
              {nvariants}
              bind:zoomLevel={$zoomLevel}
              {updateZoomLevel}
              {fullGraphView}
              bind:triggerUpdate
              {minFrequency}
              {seeMinimap}
            />
          {/key}
        {/if}
      {:else if mergingSplitting}
        <div class="no_process">Merging/Splitting Processes</div>
      {:else if selectedClusters.length === 0}
        <div class="no_process">Select Process</div>
      {:else if loading}
        <div class="loading_wrapper">
          <Loading withOverlay={false} />
        </div>
      {:else}
        {#key `${minFrequency} - ${nvariants}`}
          <FlowPlotter
            bind:this={flowPlotterElement}
            {nodesData}
            edgesData={graph["edges"]}
            variants={graph["variants"]}
            subgraphs={graph["activityMappingCluster"]}
            width="100%"
            height="100%"
            bind:selectedNode
            bind:selectedNodes
            bind:expandedNodes
            {fullGraphView}
            {fullNodeInfo}
            bind:zoomLevel={$zoomLevel}
            bind:triggerUpdate
            {nvariants}
            {minFrequency}
            {updateZoomLevel}
            executionFrequency={graph["executionFrequency"]}
            {seeMinimap}
            bind:activitiesToShow
            bind:paxbotVisible
            bind:paxbotMessage
            bind:paxbotTitle
          />
        {/key}
      {/if}
    </div>
    <div class="menu_overlay bottom">
      <div class="button_wrapper">
        <ContentSwitcher options={tabOptions} bind:selected={fullGraphView} />
      </div>
      <div class="button_wrapper">
        <!-- <ButtonPlusMinus text="Level" value={0} /> -->
        <Button
          kind="plus-minus"
          defaultValue={1}
          text="Variants"
          bind:value={nvariants}
          unit=""
          increment={(i) => {
            i = Number(i);
            return i + 1;
          }}
          decrement={(i) => {
            i = Number(i);
            if (i > 1) {
              return i - 1;
            } else {
              return i;
            }
          }}
          reset={null}
          isEditable={true}
        />
        <Button
          kind="plus-minus"
          defaultValue={0}
          text="Min. Frequency"
          bind:value={minFrequency}
          increment={(i) => {
            i = Number(i);
            if (i < 3) return i + 1;
            else {
              return Math.min(Math.round(i * 1.2), 100);
            }
          }}
          decrement={(i) => {
            i = Number(i);
            if (i <= 3) return Math.max(0, i - 1);
            else return Math.round(i / 1.2);
          }}
          unit="%"
          reset={null}
          isEditable={true}
        />
      </div>
      <div class="button_wrapper">
        <Button
          text="Zoom"
          kind="plus-minus"
          bind:value={$zoomLevel}
          showValue={false}
          unit=""
          reset={() => {
            fitView();
            updateZoomLevel();
          }}
          increment={incrementZoom}
          decrement={decrementZoom}
        />
        <Button
          kind="secondary"
          icon={FIT_SCREEN_ICON}
          on:click={() => {
            fitView();
            updateZoomLevel();
          }}
        />
        <Button
          kind="secondary"
          icon={MAP_ICON}
          on:click={() => (seeMinimap = !seeMinimap)}
        />
        <Button
          kind="secondary"
          icon={SQUARE_LANDSCAPE_ICON}
          on:click={() => {
            if (casesSelected && flowPlotterCaseElement)
              flowPlotterCaseElement.downloadSVG();
            else if (!casesSelected && flowPlotterElement)
              flowPlotterElement.downloadSVG(selectedProcesses);
          }}
        />
        <Button
          kind="secondary"
          icon={FULLSCREEN_ICON}
          on:click={toggleFullscreen}
        />
      </div>
    </div>
  </div>
  <div class="menu" slot="menu">
    {#if casesSelected}
      <CasesRightMenu {analysisid} currentActivity={selectedNode} />
    {:else}
      <Accordion
        heading={"Process Selection - " + selectedClusters.length + " selected"}
        open={true}
      >
        <ProcessSelection
          {loading}
          {processes}
          selectedHasNames
          bind:selectedProcesses
          editProcess={(id, name) => {
            editProcessId = id;
            editProcessName = name;
            editProcessModalOpen = true;
          }}
          mergeProcesses={() => {
            if (selectedClusters.length === 1) nprocesses = 2;
            else if (selectedClusters.length > 1) nprocesses = 1;
            clusteringModalOpen = true;
          }}
        />
      </Accordion>
      <Accordion heading="Activities" open={true}>
        <AddToActivity {analysisid} {activities} {activitiesToShow} />
      </Accordion>
      <Accordion heading="Applications" open={false}>
        <ProgramInfo programs={graph["uniquePrograms"]} />
      </Accordion>
      <!-- <Accordion heading="Information" open={false}>
    <div class="app_information_wrapper">
      <SideKpi name="Time" value={42} />
      <SideKpi name="Interactions" value={42} />
      <SideKpi name="Number of Variants" value={42} />
      <SideKpi name="Unique Events" value={42} />
      <SideKpi name="AVG Time" value={42} />
      <SideKpi
        name="Keyboard Inputs"
        value={42}
        showIcon={true}
        icon="keyboard_dark"
      />
      <SideKpi
        name="Mouse Inputs"
        value={42}
        showIcon={true}
        icon="mouse_dark"
      />
      <SideKpi name="Copy" value={42} showIcon={true} icon="copy_dark" />
      <SideKpi name="Paste" value={42} showIcon={true} icon="paste_dark" />
    </div>
  </Accordion> -->
    {/if}
  </div>
</SectionWrapper>

<Modal
  open={editProcessModalOpen}
  modalHeading="Edit Process"
  primaryButtonText="Save"
  secondaryButtonText="Cancel"
  actions
  preventCloseOnClickOutside
  autowidth
  on:click:button--secondary={() => {
    editProcessModalOpen = false;
  }}
  on:open
  on:close={() => {
    editProcessModalOpen = false;
  }}
  primaryButtonAction={() => {
    if (fullGraphView === 1) renameClusterApp();
    else renameCluster();
    editProcessModalOpen = false;
  }}
>
  <Button text="Auto Name" on:click={autoname} />
  <TextInput
    labelText="Name"
    placeholder="Enter name for process"
    bind:value={editProcessName}
  />
</Modal>

<Modal
  open={clusteringModalOpen}
  modalHeading="Split / Merge Processes"
  primaryButtonText="Apply"
  secondaryButtonText="Cancel"
  preventCloseOnClickOutside
  actions
  autowidth
  on:click:button--secondary={() => {
    clusteringModalOpen = false;
  }}
  on:open
  on:close={() => {
    clusteringModalOpen = false;
  }}
  primaryButtonAction={() => {
    if (fullGraphView === 1) mergeProcessesApp(selectedClusters);
    else mergeProcesses(selectedClusters);
    clusteringModalOpen = false;
  }}
>
  Please specify how many processes you want to split the selected process(es)
  into:
  <TextInput
    labelText="Number Of Processes"
    placeholder="Number of Processes"
    bind:value={nprocesses}
  />
</Modal>

<style>
  .button_wrapper {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  .menu {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    height: calc(100vh - 6rem);
    overflow-x: scroll;
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
  }
  .menu::-webkit-scrollbar {
    display: none;
  }

  .main_wrapper {
    position: relative;
    height: 100%;
    width: 100%;
    min-width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  .menu_overlay {
    position: absolute;
    width: 100%;
    z-index: 1; /* Stellt sicher, dass das Overlay über dem Canvas liegt */
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .bottom {
    bottom: 0;
  }

  .canvas {
    height: 100%;
    min-height: 100%;
    width: 100%;
    z-index: 0; /* Platziert das Canvas hinter den Overlays */
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .no_process {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
    font-size: 2rem;
    color: var(--pax-black);
  }
</style>
